import type { BaseTimelineEvent, PaymentDetail } from '../components/panels/types/panel-common.types';
import type { Quote, QuotePhase, QuoteInvoice, QuoteRevision } from '@shared/types/quotes';
import type { EventData } from '@shared/types/events.types';
import { formatDateOnly } from '@shared/utils/date';

export function generateQuoteTimelineEvents(quoteData?: Quote): BaseTimelineEvent[] {
  if (!quoteData) {
    return [];
  }

  const events: BaseTimelineEvent[] = [];
  const customerName = quoteData.customer || 'Unknown';

  events.push({
    id: `${quoteData.id}_created`,
    title: `Quote ${quoteData.quoteNumber} created for ${customerName}`,
    timestamp: formatDateOnly(quoteData.created),
    details: []
  });

  if (quoteData.send) {
    events.push({
      id: `${quoteData.id}_sent`,
      title: `Quote sent to ${customerName}`,
      timestamp: formatDateOnly(quoteData.send),
      details: []
    });
  }

  if (quoteData.status === 'accepted') {
    events.push({
      id: `${quoteData.id}_accepted`,
      title: `${customerName} accepted the quote`,
      timestamp: quoteData.send ? formatDateOnly(quoteData.send) : formatDateOnly(quoteData.created),
      details: []
    });
  }

  if (quoteData.status === 'cancelled') {
    events.push({
      id: `${quoteData.id}_cancelled`,
      title: `Quote ${quoteData.quoteNumber} was cancelled`,
      timestamp: formatDateOnly(quoteData.created),
      details: []
    });
  }

  return events.reverse();
}

export const generateQuoteGeneralData = (quoteData?: Quote): PaymentDetail[] => {
  const customer = quoteData?.customer || '-';
  const created = quoteData?.created ? formatDateOnly(quoteData.created) : '-';
  const expiration = quoteData?.expiration ? formatDateOnly(quoteData.expiration) : '-';

  return [
    {
      label: 'Customer',
      value: customer,
      isCopyable: false
    },
    {
      label: 'Created',
      value: created,
      isCopyable: false
    },
    {
      label: 'Expiration',
      value: expiration,
      isCopyable: false
    },
    {
      label: 'Payment terms',
      value: 'NET 30',
      isCopyable: false
    },
    {
      label: 'Require signing',
      value: '-',
      isCopyable: false
    },
    {
      label: 'Payment automation',
      value: '-',
      isCopyable: false
    },
    {
      label: 'Collect payment details',
      value: '-',
      isCopyable: false
    },
    {
      label: 'Collect tax information',
      value: '-',
      isCopyable: false
    },
    {
      label: 'Invoice memo',
      value: '-',
      isCopyable: false
    },
    {
      label: 'Attachments',
      value: '-',
      isCopyable: false
    }
  ];
};

export const QUOTE_PHASES_DATA: QuotePhase[] = [
  {
    id: '1',
    name: 'Initial Setup Phase',
    period: '1 Oct - 31 Oct, 2025',
    items: [
      {
        id: '1-1',
        name: '(Sample) Coconut Bar Soap',
        type: 'Retail',
        repeats: 'Monthly',
        billed: 'In arrears',
        amount: '$120.00'
      },
      {
        id: '1-2',
        name: '(Sample) Coconut Bar Soap',
        type: 'Retail',
        repeats: 'One time',
        billed: 'In arrears',
        amount: '$120.00'
      },
      {
        id: '1-3',
        name: '(Sample) Coconut Bar Soap',
        type: 'Retail',
        repeats: 'Monthly',
        billed: 'In arrears',
        amount: '$120.00'
      }
    ]
  },
  {
    id: '2',
    name: 'Ongoing Service Phase',
    period: '1 Nov - 30 Nov, 2025',
    items: [
      {
        id: '2-1',
        name: '(Sample) Coconut Bar Soap',
        type: 'Retail',
        repeats: 'Monthly',
        billed: 'In arrears',
        amount: '$120.00'
      }
    ]
  }
];

export const QUOTE_INVOICES_DATA: QuoteInvoice[] = [
  {
    total: '$2500.00',
    currency: 'USD',
    status: 'pending_approval',
    invoiceNumber: 'INV-DRAFT',
    billingPeriod: '1 Oct - 31 Oct, 2025',
    sent: '-',
    due: '-',
    finalized: '-'
  },
  {
    total: '$20.00',
    currency: 'USD',
    status: 'draft',
    invoiceNumber: 'INV-DRAFT',
    billingPeriod: '1 Oct - 31 Oct, 2025',
    sent: '-',
    due: '-',
    finalized: '-'
  },
  {
    total: '$20.00',
    currency: 'USD',
    status: 'open',
    invoiceNumber: 'INV00068',
    billingPeriod: '1 Oct - 31 Oct, 2025',
    sent: '10 Oct',
    due: '10 Oct',
    finalized: '10 Oct'
  },
  {
    total: '$20.00',
    currency: 'USD',
    status: 'paid',
    invoiceNumber: 'INV00068',
    billingPeriod: '1 Nov - 30 Nov, 2025',
    sent: '20 Oct',
    due: '20 Oct',
    finalized: '20 Oct'
  },
  {
    total: '$20.00',
    currency: 'USD',
    status: 'partially_paid',
    invoiceNumber: 'INV00068',
    billingPeriod: '1 Nov - 30 Nov, 2025',
    sent: '20 Oct',
    due: '20 Oct',
    finalized: '20 Oct'
  }
];

export const QUOTE_REVISIONS_DATA: QuoteRevision[] = [
  {
    total: '$2500.00',
    currency: 'USD',
    status: 'open',
    quoteNumber: 'INV00068',
    created: '-'
  },
  {
    total: '$20.00',
    currency: 'USD',
    status: 'cancelled',
    quoteNumber: 'INV00068',
    created: '-'
  },
  {
    total: '$20.00',
    currency: 'USD',
    status: 'cancelled',
    quoteNumber: 'INV00068',
    created: '10 Oct, 2025'
  }
];

export const QUOTE_EVENTS_DATA: EventData[] = [
  {
    id: 'evt_001',
    eventType: 'quote.created',
    description: 'Quote created for $2,500.00',
    timestamp: '15 Nov 2025, 10:23',
    requestId: 'req_q1a2b3c4d5e6',
    status: 'succeeded'
  },
  {
    id: 'evt_002',
    eventType: 'quote.sent',
    description: 'Quote sent to customer via email',
    timestamp: '15 Nov 2025, 10:30',
    requestId: 'req_q7f8g9h0i1j2',
    status: 'succeeded'
  },
  {
    id: 'evt_003',
    eventType: 'quote.viewed',
    description: 'Customer viewed the quote',
    timestamp: '16 Nov 2025, 14:15',
    requestId: 'req_q3k4l5m6n7o8',
    status: 'succeeded'
  }
];
